import { MutationCache, QueryClient } from "@tanstack/react-query";
import { noteMutationFailed } from "./unhandled-failures";

/**
 * Singleton QueryClient — imported by both the Provider and mutation hooks
 * that need to call `invalidateQueries` outside of React components.
 */
export const queryClient = new QueryClient({
  /**
   * A failed action the page does not put on screen is shown anyway
   * (lib/unhandled-failures.ts). `meta: { silentError: true }` opts a mutation
   * out, for a background save whose failure is reported some other way.
   */
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (mutation.meta?.silentError === true) return;
      noteMutationFailed(err);
    },
  }),
  defaultOptions: {
    queries: {
      /**
       * Data stays "fresh" for 30 s. During that window, navigating back to
       * a page shows cached data instantly without a network request.
       */
      staleTime: 30_000,
      /**
       * Unused cache entries are kept for 5 min before garbage-collection.
       * Returning to a page within 5 min shows the stale data while revalidating.
       */
      gcTime: 5 * 60_000,
      /**
       * One automatic retry — enough to survive a transient 5xx.
       *
       * Most 4xx is not retried: a 403 from a role gate or a 404 is a settled
       * answer, and retrying doubles the 4xx volume every page contributes
       * (see docs/runbooks/4xx-rate.md) to arrive at the same response.
       *
       * 408 and 429 are the exceptions — a timeout and the per-tenant rate
       * limiter are both explicitly "try again", and the retry usually
       * succeeds once the window rolls.
       */
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        // 401 too: authFetch refreshes and retries once itself, but a token
        // that was still cached at that moment only becomes valid a beat later,
        // and the old blanket `retry: 1` was what recovered that.
        const retryable4xx = status === 401 || status === 408 || status === 429;
        if (typeof status === "number" && status >= 400 && status < 500 && !retryable4xx) {
          return false;
        }
        return failureCount < 1;
      },
      /**
       * Don't refetch just because the user switched browser tabs.
       * Dashboard data doesn't need that level of freshness.
       */
      refetchOnWindowFocus: false,
    },
    mutations: {
      /** Mutations are user-initiated actions — never auto-retry. */
      retry: 0,
    },
  },
});
